// One date rule for every date field (brief 1E-23): a date is typed MM/DD/YYYY and must be a real
// day, or it's refused with one message. Never saved garbled ("1-/1/206-" from the live
// onboarding). The screens check with the same rule before they send; the server reads with it.
// A date picker's YYYY-MM-DD is read the same way, so the existing date fields keep working.

export const DATE_REFUSED = "Type the date as MM/DD/YYYY";
export const DATE_EXAMPLE = "For example 01/06/2026";

/** The years a date may fall in: anything else is a typing slip. */
const FIRST_YEAR = 1900;
const LAST_YEAR = 2100;

const realDay = (y: number, m: number, d: number) => {
  if (y < FIRST_YEAR || y > LAST_YEAR || m < 1 || m > 12 || d < 1) return false;
  const at = new Date(Date.UTC(y, m - 1, d));
  return at.getUTCFullYear() === y && at.getUTCMonth() === m - 1 && at.getUTCDate() === d;
};
const pad = (n: number, w = 2) => String(n).padStart(w, "0");

/**
 * Reads a typed date: MM/DD/YYYY (as the screens show dates) or YYYY-MM-DD (a date picker).
 * Answers the day as YYYY-MM-DD, or null when it isn't a real day.
 */
export function dayOf(input: unknown): string | null {
  if (typeof input !== "string") return null;
  const s = input.trim();
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) {
    const [mm, dd, yy] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return realDay(yy, mm, dd) ? `${yy}-${pad(mm)}-${pad(dd)}` : null;
  }
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) {
    const [yy, mm, dd] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return realDay(yy, mm, dd) ? s : null;
  }
  return null;
}

/** Whether a stored or sent day (YYYY-MM-DD) is a real day. */
export const isRealDay = (v: unknown): v is string =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && dayOf(v) === v;

/** Reads a required date field: the day, or the refusal. */
export function readDay(
  input: unknown,
): { ok: true; value: string } | { ok: false; error: string } {
  const day = dayOf(input);
  return day ? { ok: true, value: day } : { ok: false, error: DATE_REFUSED };
}

/** Reads an optional date field: empty is null; anything else must be a real day. */
export function readOptionalDay(
  input: unknown,
): { ok: true; value: string | null } | { ok: false; error: string } {
  if (input === undefined || input === null || (typeof input === "string" && !input.trim()))
    return { ok: true, value: null };
  return readDay(input);
}

/** MM/DD/YYYY for a day, as a date field shows it. */
export const typedDay = (day: string | null) =>
  day ? `${day.slice(5, 7)}/${day.slice(8, 10)}/${day.slice(0, 4)}` : "";
